import { nonNullable } from 'lib/array/arrayUtils'
import {
  DEFAULT_MAP_OPTIONS,
  FIT_BOUNDS_PADDING,
  USER_ZOOM,
} from 'tripPlanner/config/map'
import {
  ExploreItem,
  GeoPlace,
  GeoPoint,
  PlaceGeo,
} from 'tripPlanner/types/common'
import { PublicMapItem } from 'tripPlanner/types/publicTrip'
import {
  MapItem,
  MarkerItem,
  RouteItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { isExploreItem } from 'tripPlanner/utils/explore'
import { isSamePlace } from 'tripPlanner/utils/places'

export interface BaseMapElementProps {
  position: GeoPoint
  active?: boolean
  onDeselect?: () => void
  onSelect?: () => void
  zIndex?: number
  itemPlaceType?: 'start' | 'end' | 'single'
}

export interface MapMarkerProps extends BaseMapElementProps {
  item: MarkerItem
  includeInMapBounds: boolean
}

export interface MapRouteProps extends BaseMapElementProps {
  item: RouteItem
  includeInMapBounds: boolean
}

export interface ExploreMapMarkerProps extends BaseMapElementProps {
  item: ExploreItem
  includeInMapBounds: boolean
}

export const mapToExploreMarker = (
  item: ExploreItem,
): ExploreMapMarkerProps => {
  return {
    position: item.place.point,
    item,
    includeInMapBounds: true,
  }
}

export const mapToMarker = (
  item: MarkerItem,
  includeInMapBounds: boolean = true,
): MapMarkerProps | MapMarkerProps[] => {
  if ('place' in item) {
    return {
      position: item.place.point,
      item,
      includeInMapBounds,
      itemPlaceType: 'single',
    }
  }
  if (
    item.endPlace?.type === 'GEO' &&
    !isSamePlace(item.startPlace, item.endPlace)
  ) {
    return [
      {
        position: item.startPlace.point,
        item,
        includeInMapBounds,
        itemPlaceType: 'start',
      },
      {
        position: item.endPlace.point,
        item,
        includeInMapBounds,
        itemPlaceType: 'end',
      },
    ]
  } else {
    return {
      position: item.startPlace.point,
      item,
      includeInMapBounds,
      itemPlaceType: 'single',
    }
  }
}

export const mapToRoute = (
  item: RouteItem,
  includeInMapBounds: boolean = true,
): MapRouteProps => {
  return {
    item,
    position: item.startPlace.point,
    includeInMapBounds,
  }
}

export const setMapMarkers = (
  markers: (MapMarkerProps | MapRouteProps | ExploreMapMarkerProps)[],
  map?: google.maps.Map,
  selectedPlace?: PlaceGeo,
) => {
  if (map && markers.length > 0) {
    const bounds = new google.maps.LatLngBounds()
    nonNullable(markers).forEach((marker) => {
      if (!marker.includeInMapBounds) {
        return
      }

      bounds.extend(
        new google.maps.LatLng(marker.position.lat, marker.position.lng),
      )

      if (!isExploreItem(marker.item) && isRouteItem(marker.item)) {
        bounds.extend(
          new google.maps.LatLng(
            marker.item.endPlace.point.lat,
            marker.item.endPlace.point.lng,
          ),
        )
      }
    })

    map.setOptions({
      minZoom: DEFAULT_MAP_OPTIONS.minZoom,
      maxZoom: DEFAULT_MAP_OPTIONS.maxZoom,
      center: undefined,
      zoom: undefined,
    })

    if (bounds.isEmpty() && selectedPlace) {
      bounds.extend(
        new google.maps.LatLng(
          selectedPlace?.point.lat ?? 0,
          selectedPlace?.point.lng,
        ),
      )
    }

    map.fitBounds(bounds, FIT_BOUNDS_PADDING)
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      map.setOptions({ minZoom: USER_ZOOM.min, maxZoom: USER_ZOOM.max })
    })
  } else if (map && selectedPlace && markers.length === 0) {
    map.setOptions({
      minZoom: DEFAULT_MAP_OPTIONS.minZoom,
      maxZoom: DEFAULT_MAP_OPTIONS.maxZoom,
      center: undefined,
      zoom: undefined,
    })

    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(
        selectedPlace.point.lat ?? 0,
        selectedPlace.point.lng,
      ),
    )

    map.fitBounds(bounds, FIT_BOUNDS_PADDING)
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      map.setOptions({ minZoom: USER_ZOOM.min, maxZoom: USER_ZOOM.max })
    })
  }
}

export const isPinnedItem = (
  item: MapItem | PublicMapItem,
): item is MarkerItem =>
  ((item.type === 'AIRBNB' ||
    item.type === 'ATTRACTION' ||
    item.type === 'EVENT' ||
    item.type === 'EXPERIENCE' ||
    item.type === 'HOTEL' ||
    item.type === 'RESTAURANT_BAR' ||
    item.type === 'ACCOMMODATION_RENTAL') &&
    item.place.type === 'GEO') ||
  ((item.type === 'CAR_RENTAL' ||
    item.type === 'CRUISE' ||
    item.type === 'TOUR' ||
    item.type === 'THING_TO_DO') &&
    item.startPlace.type === 'GEO')

export const isRouteItem = (item: MapItem | PublicMapItem): item is RouteItem =>
  (item.type === 'FLIGHT' ||
    item.type === 'BUS' ||
    item.type === 'FERRY' ||
    item.type === 'TRAIN' ||
    item.type === 'OTHER_TRANSPORT') &&
  item.startPlace.type === 'GEO' &&
  item.endPlace.type === 'GEO'

export const getPathBounds = (
  startPoint: google.maps.LatLngLiteral,
  endPoint: google.maps.LatLngLiteral,
) => {
  const southWestPoint: google.maps.LatLngLiteral = {
    lat: Math.min(startPoint.lat, endPoint.lat),
    lng: Math.min(startPoint.lng, endPoint.lng),
  }
  const northEastPoint: google.maps.LatLngLiteral = {
    lat: Math.max(startPoint.lat, endPoint.lat),
    lng: Math.max(startPoint.lng, endPoint.lng),
  }

  return new google.maps.LatLngBounds(southWestPoint, northEastPoint)
}

// Aligns bottom of object with lat/lng
export const pinOffSetter = (offsetWidth: number, offsetHeight: number) => ({
  x: -offsetWidth / 2,
  y: -offsetHeight + 3, // Google adds 3px of spacing from OverlayView border
})

// Centers object at lat/lng
export const centeredOffSetter = (
  offsetWidth: number,
  offsetHeight: number,
) => ({
  x: -offsetWidth / 2,
  y: -offsetHeight / 2,
})

export function isMapItem<T extends { type: TripItem['type'] }>(
  item: T,
): item is Exclude<T, { type: 'NOTE' }> {
  return item.type !== 'NOTE'
}

function addPixelOffsetToCoords(
  projection: google.maps.MapCanvasProjection,
  latLng: GeoPoint,
  offset: { x?: number; y?: number },
) {
  const point = projection.fromLatLngToContainerPixel(latLng)
  if (!point) {
    return null
  }
  const newPoint = new google.maps.Point(
    point.x + (offset.x ?? 0),
    point.y + (offset.y ?? 0),
  )
  return projection.fromContainerPixelToLatLng(newPoint)
}

// Thresholds where the pin is considered too close to the edge of the map
// and should be panned to the centre if selected.
const VERTICAL_PIN_MARGIN = 32
const HORIZONTAL_PIN_MARGIN = 16

export function panToIntoViewIfObstructed(
  map: google.maps.Map,
  marker: MapMarkerProps | ExploreMapMarkerProps,
  carouselContainer?: HTMLDivElement | null,
) {
  const overlay = new google.maps.OverlayView()
  overlay.draw = () => {}
  overlay.setMap(map)
  const projection = overlay.getProjection()

  if (projection) {
    const pixelPos = projection.fromLatLngToContainerPixel(marker.position)
    const carouselHeight = carouselContainer?.offsetHeight ?? 0
    const mapHeight = map.getDiv().offsetHeight
    const mapWidth = map.getDiv().offsetWidth

    if (
      pixelPos &&
      (pixelPos.y > mapHeight - carouselHeight - VERTICAL_PIN_MARGIN ||
        pixelPos.y < VERTICAL_PIN_MARGIN ||
        pixelPos.x > mapWidth - HORIZONTAL_PIN_MARGIN ||
        pixelPos.x < HORIZONTAL_PIN_MARGIN)
    ) {
      const panToCoords = addPixelOffsetToCoords(projection, marker.position, {
        y: carouselHeight / 2,
      })
      if (panToCoords) {
        map.panTo(panToCoords)
      }
    }
  }
}

export function startPlaceOfTripItem(item: TripItem): GeoPlace | undefined {
  if ('startPlace' in item && 'id' in item.startPlace) {
    return item.startPlace
  }
  if ('place' in item && 'id' in item.place) {
    return item.place
  }
  return undefined
}
